import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            title={article.title}
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-[200px] h-36 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            title={article.title}
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <h3 className="text-base font-semibold text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-500 line-clamp-2 mt-2 leading-relaxed">
              {article.excerpt}
            </p>
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {article.commentCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          title={article.title}
        />
        {article.isHot && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-background-50">
            热门
          </span>
        )}
        {article.isNew && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50">
            最新
          </span>
        )}
      </div>
      <div className="p-4">
        <span className="inline-block text-xs text-primary-600 font-medium mb-2 bg-primary-50 px-2 py-0.5 rounded">
          {article.tags[0]}
        </span>
        <h3 className="text-base font-semibold text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{article.publishDate}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
          </span>
        </div>
      </div>
    </Link>
  );
}